import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import classNames from 'classnames';
import _ from 'lodash';
import pino from 'pino';
import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import type { FC, HTMLAttributes, PropsWithChildren } from 'react';
import { useNavigate } from 'react-router';
import { useMedia } from 'react-use';
import { useShallow } from 'zustand/shallow';

import { sideMenuConstants } from '../../../constants/appConstants';
import { notificationStrings } from '../../../constants/strings';
import {
	UI_SIDE_MENU,
	UI_SIDE_MENU_COLLAPSED,
	UI_SIDE_MENU_MOBILE_VIEW_OPEN,
	UI_SIDE_MENU_OPEN,
} from '../../../constants/uiConstants';
import { updateApprovalsData } from '../../../queries/approvals/approvalQuery';
import { authStatusQuery, useSessionLogoutQuery } from '../../../queries/sessionQuery';
import { selectedTeamIdQuery } from '../../../queries/teamQuery';
import { userQuery } from '../../../queries/userQuery';
import { ConfigRepository } from '../../../repositories/configRepository';
import { ConversationRepository } from '../../../repositories/conversationRepository';
import { SessionRepository } from '../../../repositories/sessionRepository';
import { setTime } from '../../../utils/setTime';
import { useZustand } from '../../../zustandStore';
import type { DialogActions, User } from '../../../zustandStore/slices';

import SideMenuFooter from './sideMenuFooter';
import SideMenuHeader from './sideMenuHeader';
import SideMenuMyRequestAside from './sideMenuMyRequestAside';
import SideMenuPanel from './sideMenuPanel';
import SideMenuTeamSwitcher from './sideMenuTeamSwitcher';
import './style.less';

const UserDetailsAside = lazy(() => import('../../organisms/userDetailsAside'));

const logger = pino();
const VERSION = ConfigRepository.get('VERSION');

type SideMenuProps = HTMLAttributes<HTMLElement>;

const fetchAndUpdateStream = async (user: User, stopInterval: () => void, signal: AbortSignal) => {
	try {
		if (!user['userId']) {
			stopInterval();
			return;
		}

		const { setStreamData, setCurrentStreamData } = useZustand.getState();
		const { currentStreamData, initialAfter } = useZustand.getState().stream;

		const response = await ConversationRepository.getStream({
			after: currentStreamData.lastTimestamp || initialAfter,
			signal,
		});

		if (!_.isEmpty(response.events)) {
			setCurrentStreamData(response);
		}

		if (response.lastTimestamp && response.events.length > 0) {
			const streamData = useZustand.getState().stream.streamData;

			const newData = {
				...streamData,
				events: [...streamData.events, ...response.events],
			};

			setStreamData(newData);
			void updateApprovalsData(user, response.events);
		}
	} catch (error) {
		if (error instanceof Error && axios.isAxiosError(error) && error.status === 403) {
			if (!globalThis.IS_PLAYWRIGHT_RUNNING) {
				// prevent redirecting to login page when running playwright tests
				void SessionRepository.logout();
				globalThis.location.href = '/signin/login?inactivityLogout=true';
			}

			logger.info('User does not have permission to access stream');
			return;
		}
		if (error instanceof Error && error.name === 'AbortError') {
			logger.info('Fetch request was aborted');
			stopInterval();
			return;
		}
		logger.error({ message: 'Error in fetching stream:', error });
		stopInterval();
	}
};

const fetchServerVersion = async (
	setDialog: DialogActions['setDialog'],
	closeDialog: DialogActions['closeDialog'],
	stopInterval: () => void,
) => {
	const serverVersion = await ConfigRepository.getServerVersion();

	if (VERSION !== serverVersion) {
		stopInterval();
		let countdown = 10;

		const countdownInterval = setInterval(
			() => {
				setDialog({
					message: `${"Sorry! We're having trouble connecting at the moment - retrying in"} ${countdown} ${'seconds'}.`,
					type: notificationStrings.WARNING,
					open: true,
					actionText: 'Retry connection now',
					onAccept: () => {
						closeDialog();
						globalThis.location.reload();
					},
					showCancel: false,
				});

				countdown -= 1;

				if (countdown <= 0) {
					clearInterval(countdownInterval);
					globalThis.location.reload();
				}
			},
			setTime(1, 'seconds'),
		);
	}
};

const SideMenu: FC<PropsWithChildren<SideMenuProps>> = () => {
	const { isLoading, isSuccess } = useQuery(authStatusQuery);
	const [openAside, setOpenAside] = useState(false);
	const isMobileWidth = useMedia('(max-width: 650px)');
	const { data: userData } = useQuery(userQuery);
	const { data: userSelectedTeamData } = useQuery(selectedTeamIdQuery('selectedTeamID'));
	const intervalRef = useRef<NodeJS.Timeout | null>(null);
	const abortControllerRef = useRef<AbortController | null>(null);
	const navigate = useNavigate();

	const {
		sideMenu,
		user: userDataFromZustand,
		setDialog,
		closeDialog,
		setUser,
		setNotification,
		setCompanyName,
		setCompanyId,
		setUserSelectedTeam,
		openSideMenuOnMobileView,
	} = useZustand(
		useShallow(state => ({
			sideMenu: state.sideMenu,
			user: state.user,
			setDialog: state.setDialog,
			closeDialog: state.closeDialog,
			setUser: state.setUser,
			setNotification: state.setNotification,
			setCompanyName: state.setCompanyName,
			setCompanyId: state.setCompanyId,
			setUserSelectedTeam: state.setUserSelectedTeam,
			openSideMenuOnMobileView: state.openSideMenuOnMobileView,
		})),
	);
	const { isOpenOnDesktopView, isOpenOnMobileView } = sideMenu;

	const shouldRender = !['/signin', '/tos', '/landing'].includes(globalThis.location.pathname);

	const logout = useSessionLogoutQuery();

	const stopInterval = () => {
		if (intervalRef.current) {
			clearInterval(intervalRef.current);
			intervalRef.current = null;
		}
		if (abortControllerRef.current) {
			abortControllerRef.current.abort();
			abortControllerRef.current = null;
		}
	};

	const startInterval = () => {
		if (!userData?.userId) {
			stopInterval();
			return;
		}
		if (!intervalRef.current) {
			abortControllerRef.current = new AbortController();
			intervalRef.current = setInterval(
				async () => {
					try {
						const results = await Promise.allSettled([
							abortControllerRef.current
								? fetchAndUpdateStream(userData, stopInterval, abortControllerRef.current.signal)
								: Promise.resolve(),
							fetchServerVersion(setDialog, closeDialog, stopInterval),
						]);

						results.forEach(result => {
							if (result.status === 'rejected') {
								const error = result.reason;
								if (axios.isCancel(error)) {
									logger.warn({ message: 'Request canceled', error: error.message });
								} else {
									logger.error({ message: 'Error in fetching updates:', error });

									if (
										!window['IS_PLAYWRIGHT_RUNNING'] &&
										axios.isAxiosError(error) &&
										error.response?.status === 401 &&
										!globalThis.location.pathname.includes('signin')
									) {
										setNotification([
											{
												type: notificationStrings.ERROR,
												message: 'Your session has expired.\nYou will now be redirected to the login page.',
											},
										]);

										setTimeout(
											() => {
												void navigate('/signin/login');
											},
											setTime(2, 'seconds'),
										);
									}
								}
							}
						});
					} catch (error) {
						logger.error({ message: 'Unexpected error in fetching interval updates:', error });
					}
				},
				setTime(2, 'seconds'),
			);
		}
	};

	async function logoutApp() {
		try {
			await logout.mutateAsync();
		} catch (error) {
			logger.error({ message: 'Error in logging out', error });
		} finally {
			void navigate('/signin/login');
		}
	}

	useEffect(() => {
		if (!isLoading && !isSuccess && userData?.userId) {
			void logoutApp();
		}
	}, [isLoading, isSuccess, userData?.userId]);

	useEffect(() => {
		if (
			userSelectedTeamData &&
			userSelectedTeamData.length > 0 &&
			!userSelectedTeamData.includes(userDataFromZustand.userSelectedTeam)
		) {
			setUserSelectedTeam(userSelectedTeamData[0] || '');
		}
	}, [userDataFromZustand.userSelectedTeam, userSelectedTeamData]);

	useEffect(() => {
		if (
			userData &&
			userData.userId &&
			(userDataFromZustand.userId !== userData.userId || userDataFromZustand.avatarUrl !== userData.avatarUrl)
		) {
			setUser(userData);
			setCompanyName(userData.companyName);
			setCompanyId(userData.companyAccountId);
		}
	}, [userData, userDataFromZustand.avatarUrl, userDataFromZustand.userId]);

	useEffect(() => {
		let cleanup: (() => void) | undefined;

		const handleClick = (event: Event) => {
			// we select the hamburger menu defined in angularjs using ts.ui
			if ((event.target as HTMLElement).closest('.ts-app .ts-headerbar .ts-button')) {
				event.preventDefault();
				openSideMenuOnMobileView(true);
			}
		};

		const attachEventListener = () => {
			const headerElement = document.querySelector('.ts-app .ts-headerbar .ts-button') as HTMLElement;
			if (headerElement) {
				headerElement.addEventListener('click', handleClick);
				cleanup = () => {
					headerElement.removeEventListener('click', handleClick);
				};
			}
		};

		const observerCallback = () => {
			if (isMobileWidth) {
				attachEventListener();
			}
		};

		const observer = new MutationObserver(observerCallback);
		observer.observe(document, { childList: true, subtree: true });

		const handleResize = () => {
			if (isMobileWidth) {
				attachEventListener();
			}
		};

		window.addEventListener('resize', handleResize);

		return () => {
			cleanup?.();
			observer.disconnect();
			window.removeEventListener('resize', handleResize);
		};
	}, [openSideMenuOnMobileView, isMobileWidth]);

	useEffect(() => {
		if (document.hasFocus() && userData?.userId) {
			startInterval();
		}

		const handleFocus = () => startInterval();
		const handleBlur = () => stopInterval();

		window.addEventListener('focus', handleFocus);
		window.addEventListener('blur', handleBlur);

		return () => {
			stopInterval();
			window.removeEventListener('focus', handleFocus);
			window.removeEventListener('blur', handleBlur);
		};
	}, [userData]);

	const openUserDetailsAside = useCallback(() => {
		setOpenAside(true);
	}, []);

	const closeUserDetailsAside = () => {
		setOpenAside(false);
		if (userData) {
			setUser(userData);
		}
	};

	const sideMenuCLassNames = classNames({
		[UI_SIDE_MENU]: true,
		[UI_SIDE_MENU_COLLAPSED]: isOpenOnDesktopView !== undefined && !isOpenOnDesktopView && !isMobileWidth,
		[UI_SIDE_MENU_OPEN]: isOpenOnDesktopView === undefined || isOpenOnDesktopView || isMobileWidth,
		[UI_SIDE_MENU_MOBILE_VIEW_OPEN]: isOpenOnMobileView && isMobileWidth,
	});

	return isSuccess && shouldRender ? (
		<>
			<div id={sideMenuConstants.sideMenuId} className={sideMenuCLassNames} data-testid="side-menu">
				<SideMenuHeader />
				<SideMenuPanel />
				<SideMenuFooter openUserDetailsAside={openUserDetailsAside} />
				<SideMenuTeamSwitcher />
				<SideMenuMyRequestAside />
			</div>

			<Suspense fallback={<div />}>
				<UserDetailsAside openUserDetailsAside={openAside} closeUserAside={closeUserDetailsAside} />
			</Suspense>
		</>
	) : null;
};

export default SideMenu;
